'use client'

import { useEffect, useState } from 'react'
import { Sun, Sunrise, Clock3, CalendarDays, Compass } from 'lucide-react'
import { toMin, fmt12 } from '../../lib/prayer-next'
import { qiblaBearing, compass, CITY_COORDS } from '../../lib/qibla'

/* "Today at a glance" for the directory — city-level, all real (AlAdhan via
   /api/prayer): Hijri date, sunrise, zawal (solar-noon midpoint of
   sunrise→maghrib), Jummah at the calculated Dhuhr, and the city's Qibla
   bearing. Each row renders only when its source data exists. */
export default function DirectoryGlance({ city }: { city: string }) {
  const target = city && city !== 'All Cities' ? city : 'Hyderabad'
  const [raw, setRaw] = useState<any>(null)

  useEffect(() => {
    let alive = true
    fetch(`/api/prayer?city=${encodeURIComponent(target)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d?.Fajr) setRaw(d) })
      .catch(() => {})
    return () => { alive = false }
  }, [target])

  const rows: Array<{ icon: any; label: string; value: string; note?: string }> = []
  if (raw?.hijri) rows.push({ icon: CalendarDays, label: 'Hijri', value: String(raw.hijri).replace(/ AH$/, ''), note: 'AH' })
  if (raw?.Sunrise && toMin(raw.Sunrise) !== null) rows.push({ icon: Sunrise, label: 'Sunrise', value: fmt12(toMin(raw.Sunrise)!) })
  const sr = raw?.Sunrise ? toMin(raw.Sunrise) : null
  const mg = raw?.Maghrib ? toMin(raw.Maghrib) : null
  if (sr !== null && mg !== null) rows.push({ icon: Sun, label: 'Zawal', value: fmt12(Math.round((sr + mg) / 2)), note: 'solar noon' })
  if (raw?.Dhuhr && toMin(raw.Dhuhr) !== null) rows.push({ icon: Clock3, label: 'Jummah', value: fmt12(toMin(raw.Dhuhr)!), note: 'calculated' })
  const coords = CITY_COORDS[target]
  if (coords) { const b = qiblaBearing(coords[0], coords[1]); rows.push({ icon: Compass, label: 'Qibla', value: `${Math.round(b)}° ${compass(b)}`, note: 'from true north' }) }

  if (!rows.length) return null

  return (
    <div className="dg">
      <div className="dg-head"><Sun size={13} strokeWidth={1.6} /> Today at a glance · {target}</div>
      <div className="dg-grid">
        {rows.map((r) => {
          const Icon = r.icon
          return (
            <div key={r.label} className="dg-item">
              <span className="dg-ic"><Icon size={13} strokeWidth={1.6} /></span>
              <div><small>{r.label}</small><strong>{r.value}{r.note ? <em> {r.note}</em> : null}</strong></div>
            </div>
          )
        })}
      </div>
      <style jsx>{`
        .dg{display:grid;gap:10px}
        .dg-head{display:inline-flex;align-items:center;gap:6px;font-family:var(--f-mono,monospace);font-size:9.5px;letter-spacing:.12em;text-transform:uppercase;color:var(--f-green-deep,#0E7C61);font-weight:600}
        .dg-grid{display:grid;gap:7px}
        .dg-item{display:flex;align-items:center;gap:9px;padding:7px 9px;background:#fff;border:1px solid var(--f-line,#e9e7df);border-radius:9px}
        .dg-ic{width:26px;height:26px;border-radius:7px;display:grid;place-items:center;flex:none;background:rgba(14,124,97,.09);color:#0E7C61}
        .dg-item small{display:block;font-size:9.5px;letter-spacing:.04em;text-transform:uppercase;color:#a8a69d}
        .dg-item strong{font-size:13px;font-weight:700;color:#2a2922}
        .dg-item strong em{font-style:normal;font-size:10.5px;font-weight:500;color:#a8a69d}
      `}</style>
    </div>
  )
}
